import {
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import avatarImg1 from "../assets/userlogo.png";

import { loginRequest, msalInstance, silentRequest } from "./auth-config";

import { AuthProvider, useAuthContext } from "./auth-context";
import { graphConfig } from "../auth/auth-config";

if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
}

msalInstance.addEventCallback((event) => {
  if (
    event.eventType === EventType.LOGIN_SUCCESS &&
    !!event?.payload &&
    "account" in event.payload &&
    !!event.payload.account
  ) {
    const account = event.payload.account;

    msalInstance.setActiveAccount(account);
  }
});

function AuthWrapper({ children }: AuthProps) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <Auth>{children}</Auth>
      </AuthProvider>
    </MsalProvider>
  );
}
const acquireSilentToken = async () => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error("User not logged in");
  }
  try {
    const response = await msalInstance.acquireTokenSilent({
      account,
      ...silentRequest,
    });
    if (response.accessToken) {
      return response.accessToken;
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // fallback to interaction when silent call fails
      return msalInstance.acquireTokenPopup(silentRequest);
    }
    console.error(error);
    return "";
  }
};
const acquireApiToken = async () => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    throw new Error("User not logged in");
  }
  try {
    const response = await msalInstance.acquireTokenSilent({
      account,
      ...loginRequest,
    });
    if (response.accessToken) {
      return response.accessToken;
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // fallback to interaction when silent call fails
      return msalInstance.acquireTokenPopup(silentRequest);
    }
    console.error(error);
    return "";
  }
};

function login() {
  msalInstance.loginRedirect(loginRequest);
}

function logout() {
  msalInstance.logoutRedirect();
}
type AuthProps = { children: React.ReactNode };

const Auth = ({ children }: AuthProps) => {
  const { accounts, inProgress, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { dispatch } = useAuthContext();
  const [isRedirectHandled, setIsRedirectHandled] = useState(false);

  useEffect(() => {
    if (
      inProgress === InteractionStatus.None &&
      accounts.length > 0 &&
      isAuthenticated
    ) {
      const account = accounts[0];
      if (account) {
        const fetchProfilePic = async () => {
          let blobUrl = avatarImg1;
          try {
            const token = await acquireSilentToken();
            const { data: photoValue } = await axios.request({
              method: "GET",
              headers: { Authorization: `Bearer ${token}` },
              responseType: "blob",
              url: graphConfig.graphPhotoEndpoint,
            });
            blobUrl = window.URL.createObjectURL(photoValue);
          } catch (error) {
            console.error("Error fetching profile photo", error);
          }
          dispatch({
            type: "setUser",
            user: {
              name: account.name ?? "",
              username: account.username,
              roles: null,
              profilePicURL: blobUrl,
            },
          });
        };
        fetchProfilePic();
      }
    }
  }, [inProgress, accounts, instance, isAuthenticated, dispatch]);

  useEffect(() => {
    const handleRedirect = async () => {
      try {
        await instance.handleRedirectPromise();
        setIsRedirectHandled(true);
      } catch (error) {
        console.error("Error handling redirect:", error);
      }
    };

    if (!isRedirectHandled && inProgress === "startup") {
      handleRedirect();
    }
  }, [instance, inProgress, isRedirectHandled]);

  return <>{children}</>;
};

/* Private Route */
type PrivateRouteProps = { children: React.JSX.Element };

const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  if (!isAuthenticated) {
    navigate("/");
    return null;
  }
  return children;
};

export {
  acquireApiToken,
  acquireSilentToken,
  AuthWrapper,
  AuthenticatedTemplate,
  login,
  logout,
  PrivateRoute,
  UnauthenticatedTemplate,
};
